import { useMutation } from "@apollo/client";
import { useFormik } from "formik";
import _ from "lodash";
import { useEffect } from "react";

import {
  PROCESS_IMAGE,
  UPLOAD_IMAGE,
} from "../../../../../graphql/mutation/Image";
import useUploadProgress from "../../../../../hooks/useUploadProgress";
import { Button, getInput, Modal } from "../../../../ui";

const UploadImage = props => {
  const { artwork, isOpen, mainCloseModal } = props;
  const [uploadImage, { loading }] = useMutation(UPLOAD_IMAGE, {
    refetchQueries: ["FetchArtwork", "FetchInstance"],
  });
  const [processImage] = useMutation(PROCESS_IMAGE);

  const {
    progress,
    isUploading,
    failed: failedUploads,
    start: startUpload,
    reset: resetUpload,
  } = useUploadProgress();

  const formik = useFormik({
    initialValues: {
      images: [],
    },
    enableReinitialize: true,
  });

  const handleClick = async () => {
    const res = await uploadImage({
      variables: {
        input: {
          artworkId: artwork?.id,
          imageCount: formik?.values?.images?.length,
        },
      },
    });

    const success = res?.data?.uploadImage?.success;
    const urls = res?.data?.uploadImage?.urls;
    const images = formik?.values?.images ?? [];

    if (success && urls?.length > 0) {
      const { fetchFns, fetchFiles } = _.transform(
        urls,
        (acc, url, idx) => {
          const image = images[idx];

          if (!image?.file) {
            return;
          }

          acc.fetchFiles.push(image.file);
          acc.fetchFns.push(async (file, { signal }) => {
            await fetch(url.url, {
              method: "PUT",
              headers: new Headers({ "Content-Type": "image/tif" }),
              body: file,
              signal,
            });

            await processImage({
              variables: {
                input: {
                  archiveId: url.archiveId,
                  path: url.path,
                  artworkId: artwork?.id,
                },
              },
            });
          });
        },
        { fetchFns: [], fetchFiles: [] },
      );

      if (fetchFns.length > 0) {
        await startUpload(fetchFns, fetchFiles);
      }
    }
  };

  const handleRetry = async () => {
    await startUpload(
      failedUploads.map(({ fn }) => fn),
      failedUploads.map(({ file }) => file),
    );
  };

  useEffect(() => {
    formik?.resetForm();
    resetUpload();
  }, [isOpen]);

  const inputs = [
    {
      label: "",
      type: "img",
      isMultiSelect: true,
      value: formik?.values?.images,
      w: "w-full",
      h: "h-96",
      accept: "image/tiff, image/tif, .tiff, .tif",
      preview: false,
      disabled: isUploading || loading || progress === 100,
      onChange: evt => {
        const { files } = evt.target;

        formik?.setFieldValue(
          "images",
          Array.from(files).map(file => {
            return {
              url: URL.createObjectURL(file),
              file,
            };
          }),
        );
      },
    },
  ];

  const uploadModalProps = {
    scale: "md",
    closeOnBackdrop: false,
    hideCloseButton: true,
    artwork,
    body: ({ closeModal }) => {
      const uploadSuccess = progress === 100;

      return (
        <div className="flex w-full flex-col p-8">
          <div className="flex flex-row">
            <div className="flex flex-1 flex-col text-2xl font-bold">
              Upload Image for &quot;{artwork?.title}&quot;
            </div>
            <div className="flex flex-row">
              <Button
                action={uploadSuccess ? "primary" : "default"}
                className={`mr-8 ${uploadSuccess ? "bg-green-600 hover:bg-green-700" : ""}`}
                label={uploadSuccess ? "Done" : "Cancel"}
                onClick={uploadSuccess ? mainCloseModal : closeModal}
              />
              {!uploadSuccess && failedUploads.length === 0 && (
                <Button
                  label={loading || isUploading ? "Uploading" : "Upload"}
                  disabled={
                    loading || isUploading || formik?.values?.images.length < 1
                  }
                  onClick={handleClick}
                />
              )}
            </div>
          </div>
          {isUploading && (
            <div className="mt-6 w-full">
              <div className="mb-1 flex justify-between">
                <span className="text-lg font-bold tracking-wide text-gray-600">
                  {Math.round(progress)}%
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-black transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
          {failedUploads.length > 0 && (
            <div className="mt-6 rounded-md bg-red-50 p-4 text-red-800">
              <p className="font-semibold">
                {failedUploads.length} uploads failed;
                <ul>
                  {failedUploads.map(({ file = {} }) => {
                    const { name, size } = file;
                    return <li key={size}>{name}</li>;
                  })}
                </ul>
              </p>
              <Button
                className="mt-2"
                label="Retry Failed"
                onClick={handleRetry}
              />
            </div>
          )}
          <div className="grid w-full grid-cols-1 pb-10 pt-20">
            <div className="flex">
              {inputs?.map((item, index) => {
                return (
                  <div key={index} className={`w-full ${item?.className}`}>
                    {getInput(item)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    },
  };

  return (
    <Modal {...uploadModalProps}>
      <Button label="Upload Image" action="black" />
    </Modal>
  );
};

export default UploadImage;
